import { FormikErrors } from 'formik';

/**
 * Checks for unique field values within a dataset against form values, excluding the current item being edited.
 * This ensures the current item can retain its original value if it's not being changed.
 *
 * @param {Partial<T>[] | undefined} props.dataset - Dataset to validate against.
 * @param {(keyof Partial<T>)[]} props.fieldsToCheck - Fields to check for uniqueness.
 * @param {Partial<T>} props.values - Current form values.
 * @param {string} [props.currentItemId] - Optional ID of the current item being edited to exclude from the uniqueness check.
 * @param {(values: Partial<T>, element: Partial<T>) => boolean} [props.datasetFilter] - Optional filter function to apply to the dataset before checking uniqueness.
 * @returns {FormikErrors<T>} Errors object with keys as field names and values as error messages for duplicates.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function checkFieldUniqueness<T extends Record<string, any>>({
  dataset,
  fieldsToCheck,
  values,
  currentItemId,
  datasetFilter,
}: {
  dataset: Partial<T>[] | undefined;
  fieldsToCheck: (keyof Partial<T>)[];
  values: Partial<T>;
  currentItemId?: string;
  datasetFilter?: (values: Partial<T>, element: Partial<T>) => boolean;
}): FormikErrors<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errors: Record<string, any> = {};

  if (!dataset) {
    fieldsToCheck.forEach((field) => {
      const fieldName = String(field);
      errors[fieldName] = 'Missing data to check for uniqueness.';
    });
    return errors as FormikErrors<T>;
  }

  const customFilteredDataset = datasetFilter
    ? dataset.filter((el) => datasetFilter(values, el))
    : dataset;
  const filteredDataset = currentItemId
    ? customFilteredDataset.filter((item) => item.id !== currentItemId)
    : customFilteredDataset;

  fieldsToCheck.forEach((field) => {
    const fieldName = String(field);
    if (filteredDataset.some((item) => item[field] === values[field])) {
      const fieldNameCapitalized =
        fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
      errors[fieldName] = `${fieldNameCapitalized} already exists.`;
    }
  });

  return errors as FormikErrors<T>;
}
